import { useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Chip,
  TablePagination,
  Box,
} from "@mui/material";
import { Visibility as EyeIcon, Edit as EditIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DEAL_HEADERS } from "../../utils/constants";
import { getPriorityColor, getUserInfo } from "../../utils/helpers";

export const DealsTable = ({ deals = [], stages, onViewDeal }) => {
  const navigate = useNavigate();
  const { userId, userName } = getUserInfo();
  const isTargetClient = userId === "69afdc065323c68fd76cdabf";

  const filteredHeaders = DEAL_HEADERS.filter(header => {
    const isComplaintHeader = [
      "LOGGED BY", "CALLER NAME", "PAYMENT TYPE", "MACHINE #",
      "REASON FOR CONTACT", "COMPLAINT TYPE", "ERROR CODE",
      "JUICES PUSHED COUNT", "CREDIT NOTE JUICES COUNT", "COMMAND CODE",
      "STATUS", "REFUNDS (AED)", "COMMENTS"
    ].includes(header);
    return isTargetClient || !isComplaintHeader;
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedDeals = deals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ boxShadow: "none", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
      <TableContainer sx={{
        overflow: "auto",
        maxWidth: '100%',
        // Hide scrollbar
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none', // IE and Edge
        scrollbarWidth: 'none', // Firefox
      }}>
        <Table sx={{ minWidth: isTargetClient ? 2000 : 900, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F9FAFB" }}>
              {filteredHeaders.map((header) => {
                let width = 150;
                if (header === "DEAL NAME") width = 200;
                if (header === "COMPANY NAME") width = 180;
                if (header === "PHONE NO") width = 150;
                if (header === "EMAIL") width = 200;
                if (header === "STAGE") width = 120;
                if (header === "PRIORITY") width = 100;
                if (header === "DATE") width = 120;
                if (header === "LOGGED BY") width = 150;
                if (header === "CALLER NAME") width = 150;
                if (header === "PAYMENT TYPE") width = 150;
                if (header === "MACHINE #") width = 120;
                if (header === "REASON FOR CONTACT") width = 200;
                if (header === "COMPLAINT TYPE") width = 150;
                if (header === "ERROR CODE") width = 120;
                if (header === "JUICES PUSHED COUNT") width = 160;
                if (header === "CREDIT NOTE JUICES COUNT") width = 180;
                if (header === "COMMAND CODE") width = 120;
                if (header === "STATUS") width = 150;
                if (header === "REFUNDS (AED)") width = 130;
                if (header === "COMMENTS") width = 250;
                if (header === "ACTIONS") width = 100;

                return (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap", width: width }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#475569", fontSize: "0.7rem", letterSpacing: "0.03em" }}>
                      {header}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedDeals.map((deal) => {
              const priorityColors = getPriorityColor(deal.priority);
              return (
                <TableRow key={deal.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={deal.dealName}
                    >
                      {deal.dealName}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                    <Typography variant="body2" sx={{ color: "#475569" }} title={deal.companyName || deal.company?.name || "—"}>
                      {deal.companyName || deal.company?.name || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" sx={{ color: "#475569" }}>{deal.mobile}</Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" sx={{ color: "#475569" }}>{deal.email || deal.customerEmailAddress || "—"}</Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      label={deal.stageName || "New"}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: "0.65rem", borderRadius: "6px" }}
                    />
                    {deal.closingDate && (
                      <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontSize: "0.65rem", mt: 0.5 }}>
                        {new Date(deal.closingDate).toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" })}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      label={deal.priority?.toUpperCase() || "MEDIUM"}
                      size="small"
                      sx={{
                        bgcolor: priorityColors.light,
                        color: priorityColors.text,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        borderRadius: "6px"
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                      {new Date(deal.createdAt).toLocaleDateString("en-AE", { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>

                  {isTargetClient && (
                    <>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.loggedBy || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.callerName || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.paymentType || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.machineNumber || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.reasonForContact || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.complaintType || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.errorCode || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.juicesPushed || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.creditNoteJuices || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.commandCode || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.complaintStatus || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.refundAmount || "—"}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#64748b",
                            fontSize: "0.8rem"
                          }}
                          title={deal.complaintComments}
                        >
                          {deal.complaintComments || "—"}
                        </Typography>
                      </TableCell>
                    </>
                  )}

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`${deal._id || deal.id}`)}
                        sx={{ color: "#6366f1", "&:hover": { bgcolor: "#f5f3ff" } }}
                        title="Edit Lead"
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedDeals.length === 0 && (
              <TableRow>
                <TableCell colSpan={filteredHeaders.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No deals found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={deals.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ borderTop: "1px solid #f1f5f9" }}
      />
    </Paper>
  );
};
